import {Box} from "@mui/material";
import {type MouseEvent, useCallback, useEffect, useRef, useState} from "react";
import SidebarContent from "./SidebarContent";
import {DASHBOARD_SIDEBAR_WIDTH, EXPANDED_SIDEBAR_WIDTH} from "./sidebarLayout";
import type {UserMenuPointerPosition} from "./UserMenu";

const DashboardSidebar = () => {
    const sidebarRef = useRef<HTMLDivElement | null>(null);
    const lastPointerPositionRef = useRef<UserMenuPointerPosition | undefined>(undefined);
    const suppressHoverUntilPointerExitRef = useRef(false);
    const [hovered, setHovered] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const expanded = hovered || userMenuOpen;

    const isPointerInsideCollapsedRail = useCallback((pointerPosition?: UserMenuPointerPosition) => {
        const sidebar = sidebarRef.current;
        if (!sidebar || !pointerPosition) return false;

        const rect = sidebar.getBoundingClientRect();

        return pointerPosition.x >= rect.left
            && pointerPosition.x <= rect.left + DASHBOARD_SIDEBAR_WIDTH
            && pointerPosition.y >= rect.top
            && pointerPosition.y <= rect.bottom;
    }, []);

    const handleMouseEnter = useCallback((event: MouseEvent<HTMLDivElement>) => {
        const pointerPosition = {x: event.clientX, y: event.clientY};
        lastPointerPositionRef.current = pointerPosition;

        if (suppressHoverUntilPointerExitRef.current) {
            if (!isPointerInsideCollapsedRail(pointerPosition)) {
                suppressHoverUntilPointerExitRef.current = false;
            }
            return;
        }

        setHovered(true);
    }, [isPointerInsideCollapsedRail]);

    const handleMouseMove = useCallback((event: MouseEvent<HTMLDivElement>) => {
        const pointerPosition = {x: event.clientX, y: event.clientY};
        lastPointerPositionRef.current = pointerPosition;

        if (
            suppressHoverUntilPointerExitRef.current
            && !isPointerInsideCollapsedRail(pointerPosition)
        ) {
            suppressHoverUntilPointerExitRef.current = false;
        }
    }, [isPointerInsideCollapsedRail]);

    // Recover when a remount or portal prevents the sidebar's mouse-leave event.
    useEffect(() => {
        if (!hovered) return undefined;

        const handlePointerMove = (event: PointerEvent) => {
            const sidebar = sidebarRef.current;
            if (!sidebar) return;

            const rect = sidebar.getBoundingClientRect();
            const inside = event.clientX >= rect.left
                && event.clientX <= rect.right
                && event.clientY >= rect.top
                && event.clientY <= rect.bottom;

            if (!inside) setHovered(false);
        };

        document.addEventListener("pointermove", handlePointerMove, {passive: true});
        return () => document.removeEventListener("pointermove", handlePointerMove);
    }, [hovered]);

    const handleMouseLeave = useCallback(() => {
        suppressHoverUntilPointerExitRef.current = false;
        setHovered(false);
    }, []);

    const handleUserMenuOpenChange = useCallback((
        open: boolean,
        pointerPosition?: UserMenuPointerPosition
    ) => {
        setUserMenuOpen(open);

        if (open) {
            suppressHoverUntilPointerExitRef.current = false;
            return;
        }

        const finalPointerPosition = pointerPosition ?? lastPointerPositionRef.current;
        suppressHoverUntilPointerExitRef.current = isPointerInsideCollapsedRail(finalPointerPosition);
        setHovered(false);
    }, [isPointerInsideCollapsedRail]);

    return (
        <Box
            ref={sidebarRef}
            className="hide-on-print"
            onMouseEnter={handleMouseEnter}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            sx={{
                position: "fixed",
                top: "var(--banner-offset, 0px)",
                left: 0,
                bottom: 0,
                width: expanded ? EXPANDED_SIDEBAR_WIDTH : DASHBOARD_SIDEBAR_WIDTH,
                bgcolor: "background.paper",
                zIndex: 500,
                overflow: "hidden",
                borderRight: "1px solid rgba(15, 23, 42, 0.08)",
                boxShadow: expanded ? "12px 0 32px rgba(15, 23, 42, 0.12)" : "0 0 0 rgba(15, 23, 42, 0)",
                transition: "width 210ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 210ms ease",
            }}
        >
            <SidebarContent expanded={expanded} onUserMenuOpenChange={handleUserMenuOpenChange}/>
        </Box>
    );
};

export default DashboardSidebar;
